/**
 * LEDPlaylistView — Club In-Game LED Content Manager
 *
 * Reads/writes exclusively to the club's own Airtable LED Entitlements table.
 * Fixtures are derived from the LED table's Airtable views.
 * No commercial playlist manager interaction — all data stays in the club base.
 */

import React, { useState, useCallback, useEffect } from "react";
import { THEME, FONTS } from "../constants/theme";
import { AFL_CLUBS, NRL_CLUBS } from "../config/clubBases";
import { fetchAllAssets, createLedRow, deleteLedRow, updateLedRow, fetchAllLedCommercialEntitlements } from "../airtable";
import { fetchAvailableViews, viewNameToFixtureKey } from "../services/viewLoader";
import { useAuth } from "../context/AuthContext";
import RibbonPreviewModal from "../components/ui/RibbonPreviewModal";
import ThreeSixtyPreviewModal from "../components/ui/ThreeSixtyPreviewModal";

// ─── Club identity ───
const CLUB_CODE = (process.env.REACT_APP_DEFAULT_CLUB || "").toUpperCase();
const ALL_CLUBS = { ...AFL_CLUBS, ...NRL_CLUBS };
const CLUB_CONFIG = ALL_CLUBS[CLUB_CODE] || {};
const CLUB_NAME = CLUB_CONFIG.name || "";

// ─── Helpers ───

// Build team name → code lookup (e.g. "Carlton Blues" → "CAR", "Richmond Tigers" → "RIC")
const TEAM_NAME_TO_CODE = {};
// Build code → club lookup including altCodes (e.g. "NM" → North Melbourne config)
const CODE_TO_CLUB = {};
Object.values(ALL_CLUBS).forEach(c => {
  if (c.name && c.code) {
    TEAM_NAME_TO_CODE[c.name.toLowerCase()] = c.code;
    CODE_TO_CLUB[c.code] = c;
    // Also map with common suffixes stripped
    const base = c.name.replace(/ (Blues|Tigers|Magpies|Bombers|Dockers|Suns|Cats|Giants|Hawks|Demons|Kangaroos|Power|Saints|Swans|Eagles|Bulldogs|Lions|Crows)$/i, "");
    if (base !== c.name) TEAM_NAME_TO_CODE[base.toLowerCase()] = c.code;
    // Register altCodes
    if (c.altCodes) {
      c.altCodes.forEach(alt => { CODE_TO_CLUB[alt.toUpperCase()] = c; });
    }
  }
});

/** Convert "Carlton Blues" → "CAR" etc. */
function teamCode(teamName) {
  if (!teamName) return "XXX";
  const lower = teamName.toLowerCase().trim();
  return TEAM_NAME_TO_CODE[lower] || TEAM_NAME_TO_CODE[lower.split(" ")[0]] || teamName.substring(0, 3).toUpperCase();
}

/** Build fixture key from picker fixture: "R1" + "Carlton Blues" + "Richmond Tigers" + "MCG" → "RD1_CAR_v_RIC_26" */
function buildFixtureKey(fixture) {
  const round = (fixture.round || "").replace(/^R/, "RD").replace(/^Round\s*/i, "RD");
  const home = teamCode(fixture.homeTeam);
  const away = teamCode(fixture.awayTeam);
  const venue = (fixture.venue || "MCG").replace(/\s+/g, "").replace(/Stadium$/i, "");
  const venueCode = venue === "MarvelStadium" || venue === "Marvel" ? "MRV" : venue;
  const viewName = `${round}_${home}_v_${away}_${venueCode}`;
  return viewNameToFixtureKey(viewName);
}
const FONT = FONTS.family;

// ─── Date parser — handles d/m/yyyy, yyyy-mm-dd, ISO ───
const parseDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  const slashMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (raw) => {
  const d = parseDate(raw);
  if (!d) return "";
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
};

// ─── Round sort order ───
const roundSortKey = (round) => {
  if (!round) return 999;
  const r = String(round).toUpperCase().trim();
  if (r === "OR") return 0;
  if (r === "QF" || r === "EF") return 100;
  if (r === "PF") return 101;
  if (r === "SF") return 102;
  if (r === "GF") return 103;
  const match = r.match(/R?(\d+)/);
  return match ? parseInt(match[1], 10) : 50;
};

// ─── Venue badge colors ───
const venueBadge = (venue) => {
  const v = String(venue || "").toLowerCase();
  if (v.includes("mcg")) return { bg: "#dbeafe", color: "#1e40af", label: "MCG" };
  if (v.includes("marvel")) return { bg: "#f3e8ff", color: "#7c3aed", label: "MRV" };
  if (v.includes("scg")) return { bg: "#fef3c7", color: "#92400e", label: "SCG" };
  return { bg: THEME.surface2, color: THEME.text, label: venue || "—" };
};

// ─── Slot → minutes conversion (assumes 30s default) ───
const slotsToMinutes = (count, durationSec = 30) => {
  const mins = (count * durationSec) / 60;
  return mins % 1 === 0 ? `${mins}` : mins.toFixed(1);
};

// ─── Content classification helpers ───
const parseContentType = (name) => {
  if (!name) return "";
  const lower = name.toLowerCase();
  if (lower.includes("-360") || lower.includes("_360")) return "360";
  if (lower.includes("-tiled") || lower.includes("_tiled")) return "Tiled";
  if (lower.includes("-iptv")) return "IPTV";
  return "";
};

const CONTENT_TYPE_STYLE = {
  "360":   { color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd", label: "360°" },
  "Tiled": { color: "#0d9488", bg: "#ccfbf1", border: "#5eead4", label: "TILED" },
  "IPTV":  { color: "#0369a1", bg: "#e0f2fe", border: "#7dd3fc", label: "IPTV" },
};

const parseSlotSource = (contentName, destination) => {
  // Check destination first, then content name for "-commercial" suffix
  const str = (destination || contentName || "").toLowerCase();
  if (!str) return "";
  if (str.includes("-commercial")) return "Commercial";
  return "Club";
};

const SOURCE_BADGE = {
  Club:       { color: "#d97706", bg: "#fef3c7", border: "#fcd34d" },
  Commercial: { color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
};

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function LEDPlaylistView({ selectedRound, onHeaderInfoChange }) {
  const { isLumen8Staff } = useAuth();
  const [fixtures, setFixtures] = useState([]);
  const [slots, setSlots] = useState([]);              // all club LED slots
  const [content, setContent] = useState([]);           // available LED content
  const [thumbMap, setThumbMap] = useState({});          // contentId/name → thumbnail URL
  const [assetMap, setAssetMap] = useState({});           // contentId/name → full asset object
  const [activePreview, setActivePreview] = useState(null); // { type: 'tiled'|'360', asset, ribbonItems }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qaProcessing, setQaProcessing] = useState(null); // slot ID being actioned

  // Picker state
  const [pickerFixture, setPickerFixture] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerPlays, setPickerPlays] = useState(4); // LED default: 4 rotations across game
  const [pickerQuarters, setPickerQuarters] = useState(["Q1", "Q2", "Q3", "Q4"]); // Default: all quarters

  // Auto-select quarters based on rotation count
  const setPlaysWithQuarters = useCallback((n) => {
    setPickerPlays(n);
    if (n <= 1) setPickerQuarters(["Q1"]);
    else if (n === 2) setPickerQuarters(["Q1", "Q2"]);
    else if (n === 3) setPickerQuarters(["Q1", "Q2", "Q3"]);
    else setPickerQuarters(["Q1", "Q2", "Q3", "Q4"]); // 4, 6, 8 → all quarters
  }, []);
  const [pickerCategory, setPickerCategory] = useState("In-Game LED Commercial Entitlements"); // or "Bonus Entitlement"
  const [adding, setAdding] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  // ─── Fetch data ───
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // ─── Fetch fixtures from LED table views + club LED rows + assets in parallel ───
      const [viewNames, clubLedRaw] = await Promise.all([
        fetchAvailableViews('led'),
        fetchAllLedCommercialEntitlements().catch(err => {
          console.warn("[LEDPlaylistView] Could not fetch club LED entitlements:", err.message);
          return [];
        }),
      ]);

      // 1. Parse fixtures from view names (e.g. "RD3_CAR_v_MEL_MCG")
      // Filter to only this club's fixtures (LED table may be shared across clubs)
      const parsed = viewNames
        .filter((viewName) => {
          if (!CLUB_CODE) return true; // No club filter if not configured
          const parts = viewName.match(/^(?:RD?\d+|OR|QF|PF|SF|GF|EF)_([A-Z]+)_v_([A-Z]+)_/i);
          if (!parts) return false;
          const [, home, away] = parts;
          const clubCodes = new Set([CLUB_CODE, ...(CLUB_CONFIG.altCodes || []).map(c => c.toUpperCase())]);
          return clubCodes.has(home.toUpperCase()) || clubCodes.has(away.toUpperCase());
        })
        .map((viewName) => {
          // Parse: RD3_CAR_v_MEL_MCG → round=R3, home=CAR, away=MEL, venue=MCG
          const parts = viewName.match(/^(RD?\d+|OR|QF|PF|SF|GF|EF)_([A-Z]+)_v_([A-Z]+)_(.+)$/i);
          if (!parts) return null;
          const [, roundRaw, homeCode, awayCode, venueCode] = parts;
          const round = roundRaw.replace(/^RD/, "R");
          // Resolve team codes to full names (includes altCodes like NM → North Melbourne)
          const homeClub = CODE_TO_CLUB[homeCode.toUpperCase()];
          const awayClub = CODE_TO_CLUB[awayCode.toUpperCase()];
          const venue = venueCode === "MRV" ? "Marvel Stadium" : venueCode === "MCG" ? "MCG" : venueCode;
          const fixtureKey = viewNameToFixtureKey(viewName);
          return {
            id: viewName, // Use view name as fixture ID
            round,
            venue,
            homeTeam: homeClub?.name || homeCode,
            awayTeam: awayClub?.name || awayCode,
            homeCode,
            awayCode,
            date: "",
            viewName,
            fixtureKey,
          };
        })
        .filter(Boolean)
        .sort((a, b) => roundSortKey(a.round) - roundSortKey(b.round));
      setFixtures(parsed);

      // Push round list to header
      const availableRounds = [...new Set(parsed.map(f => f.round).filter(Boolean))]
        .sort((a, b) => roundSortKey(a) - roundSortKey(b));
      if (onHeaderInfoChange) {
        onHeaderInfoChange((prev) => ({
          ...prev,
          rounds: availableRounds,
          selectedRound: prev.selectedRound || availableRounds[0] || "ALL",
        }));
      }

      // 2. Parse club LED rows — match to fixtures via Fixtures field
      const clubLedSlots = clubLedRaw.map((r) => {
        const f = r.fields || {};
        // With cellFormat=string, Fixtures returns the display value (e.g. "RD1_CAR_v_RIC_26")
        // It may be a comma-separated string if multiple fixtures, or a single value
        const fixtureRaw = String(f["Fixtures"] || "").split(",")[0].trim();
        // Find matching fixture by comparing fixture keys
        const matchedFixture = parsed.find(fx => fx.fixtureKey === fixtureRaw);
        const matchedId = matchedFixture ? matchedFixture.id : "";

        return {
          id: r.id,
          playlistId: matchedId, // Use fixture view name as the grouping key
          breakName: String(f["Breaks"] || ""),
          position: parseInt(f["Cue ID"]) || 0,
          client: f["Client or Sponsor Name"] || CLUB_NAME,
          contentName: f["Name"] || "",
          contentId: f["Allocation ID"] || "",
          slotType: "club",
          category: f["Category"] || "In-Game LED Commercial Entitlements",
          duration: "30s",
          status: f["Content Status"] || "New Submission",
          rotations: parseInt(f["Rotations"]) || 1,
          destination: f["LED - All Types"] || "",
          _fromClubLed: true,
        };
      });

      setSlots(clubLedSlots);

      const linked = clubLedSlots.filter(s => s.playlistId);
      const unlinked = clubLedSlots.filter(s => !s.playlistId);
      console.log(`[LEDPlaylistView] ${parsed.length} fixtures, ${clubLedSlots.length} club LED rows (${linked.length} linked, ${unlinked.length} unlinked)`);
      if (unlinked.length > 0) {
        console.warn(`[LEDPlaylistView] ${unlinked.length} club LED row(s) with no matching fixture:`,
          unlinked.slice(0, 3).map(s => ({ id: s.id, name: s.contentName })));
      }
    } catch (err) {
      console.error("[LEDPlaylistView] Fetch error:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onHeaderInfoChange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── QA Actions (Lumen8 staff only) ───
  const handleQAAction = useCallback(async (item, newStatus) => {
    if (qaProcessing) return;
    if (newStatus === "Rejected" && !window.confirm(`Reject "${item.contentName}"? The club will need to delete and re-allocate.`)) return;
    if (newStatus === "Pending QA" && !window.confirm(`Unlock "${item.contentName}"?\n\nThis will revert to Pending QA and allow the club to modify.`)) return;
    setQaProcessing(item.ids[0]);
    try {
      for (const id of item.ids) {
        await updateLedRow(id, { contentStatus: newStatus });
      }
      setSlots((prev) =>
        prev.map((s) => item.ids.includes(s.id) ? { ...s, status: newStatus } : s)
      );
    } catch (err) {
      console.error("[LED QA] Action failed:", err);
      alert("Failed to update status. Please try again.");
    } finally {
      setQaProcessing(null);
    }
  }, [qaProcessing]);

  // ─── Delete handler — deletes from club LED Entitlements table ───
  const handleDeleteSlots = useCallback(async (item) => {
    const label = `Remove "${item.contentName}" (×${item.count})?`;
    if (!window.confirm(label)) return;
    
    for (const id of item.ids) {
      try {
        await deleteLedRow(id);
      } catch (err) {
        console.error(`[LEDPlaylistView] Failed to delete club LED row ${id}:`, err);
        alert(`Failed to delete row: ${err.message}`);
        return; // Stop on first failure
      }
    }
    
    // Only update local state after all deletes succeed
    setSlots((prev) => prev.filter((s) => !item.ids.includes(s.id)));
  }, []);

  // ─── Load content library eagerly ───
  const loadContent = useCallback(async () => {
    if (content.length > 0) return;
    setContentLoading(true);
    try {
      const assetsMap = await fetchAllAssets();
      const allAssets = Array.from(assetsMap.values());

      // Filter for LED tiled/360 content only — excludes superscreen, big screen, ribbon, etc.
      // Destinations are venue-prefixed (e.g. "MCG-tiled", "Marvel-360-commercial").
      // Match by suffix so it works for any venue prefix.
      const ALLOWED_LED_SUFFIXES = [
        "-tiled",
        "-tiled-commercial",
        "-360",
        "-360-commercial",
      ];
      const ledAssets = allAssets
        .filter((a) => !a.archived)
        .filter((a) => {
          const dest = (a.destination || "").toLowerCase().trim();
          if (!dest) return false;
          return ALLOWED_LED_SUFFIXES.some((suffix) => dest.endsWith(suffix));
        })
        .map((a) => ({
          id: a.id,
          name: a.title || "",
          contentId: a.lumenId || "",
          client: CLUB_NAME,
          venue: "",
          duration: "30",
          destination: a.destination || "",
          thumbnail: a.thumb || "",
          tier: a.tier || "",
          category: a.category || "",
          colour: a.primaryColour || "",
          filePath: a.filePath || "",
          groupedAssetLinks: a.groupedAssetLinks || "",
        }));

      setContent(ledAssets);

      // Build thumbnail + asset lookup maps
      const tMap = {};
      const aMap = {}; // contentId/name → full asset object (for preview + badge)
      allAssets.forEach((a) => {
        if (a.thumb) {
          if (a.lumenId) tMap[a.lumenId] = a.thumb;
          if (a.title) tMap[a.title] = a.thumb;
        }
        // Key by every possible identifier for robust lookups
        if (a.lumenId) aMap[a.lumenId] = a;
        if (a.title) aMap[a.title] = a;
        if (a.id) aMap[a.id] = a; // Airtable record ID
        // Also key by destination (e.g. "mcg-360-commercial") for destination-based lookups
        // Key by "LUMENID_rest-of-title" patterns — some names include the lumen ID prefix
        if (a.lumenId && a.title && a.title !== a.lumenId) {
          // e.g. title = "CAR_8JNBYM0_BB_Great_Southern_Bank_MCG-360"
          // Also index by the full title with any suffix variations
          const titleLower = a.title.toLowerCase();
          if (!aMap[titleLower]) aMap[titleLower] = a;
        }
      });
      setThumbMap(tMap);
      setAssetMap(aMap);

      console.log(`[LEDPlaylistView] ${ledAssets.length} LED content items from club base`);
    } catch (err) {
      console.error("[LEDPlaylistView] Content fetch error:", err);
    } finally {
      setContentLoading(false);
    }
  }, [content.length]);

  useEffect(() => { loadContent(); }, [loadContent]);

  // ─── Open picker for a fixture ───
  const openPicker = useCallback((fixture) => {
    setPickerFixture(fixture);
    setPickerSearch("");
    setPickerPlays(4);
    setPickerQuarters(["Q1", "Q2", "Q3", "Q4"]);
    setPickerCategory("In-Game LED Commercial Entitlements");
    loadContent();
  }, [loadContent]);

  // ─── Add content to fixture ───
  // Writes club LED slots to the playlist slots table with Slot Type: "club"
  // and Content Status: "Pending QA". These show in Commercial QA for spec review.
  // Separate from commercial booking lines — this is the club's content wishlist.
  const handleAddContent = useCallback(async (contentItem) => {
    if (!pickerFixture || adding) return;
    setAdding(true);

    try {
      // Write to the club LED Entitlements table — NOT the commercial playlist.
      // Creates ONE row with Rotations set to the play count (e.g. ×4 = Rotations: 4).
      // CommercialQA reviews these in the club LED view. They never touch commercial playlists.
      //
      // Build the fixture key (e.g. "RD1_CAR_v_RIC_26") from the picker fixture fields.
      // This matches the format used by the club's Fixtures table so the row links correctly
      // and appears in the fixture-specific Airtable view for CommercialQA.
      // Use the fixture key that was parsed from the actual Airtable view name.
      // This preserves the original team codes (e.g. NM for North Melbourne)
      // rather than re-deriving from team names which may produce a different code.
      const fixtureKey = pickerFixture.fixtureKey || buildFixtureKey(pickerFixture);
      const fixtureName = `${pickerFixture.round} · ${pickerFixture.homeTeam} v ${pickerFixture.awayTeam}`;
      console.log(`[LEDPlaylistView] Fixture key: ${fixtureKey} (from ${fixtureName})`);

      const payload = {
        name: contentItem.name || "",
        clientSponsor: contentItem.client || CLUB_NAME,
        rotations: pickerPlays,
        breaks: pickerQuarters, // Q1, Q2, Q3, Q4 multi-select
        allocationId: contentItem.contentId || "", // Lumen8 ID
        clubInstructions: fixtureName,
        contentStatus: "New Submission",
        category: pickerCategory || "In-Game LED Commercial Entitlements",
        fixture: fixtureKey,
      };
      const record = await createLedRow(payload);
      
      if (!record) {
        throw new Error("Airtable did not return a record — row may not have been created");
      }
      
      // Verify the fixture was actually linked
      const createdFixtures = record.fields?.["Fixtures"];
      const fixtureLinked = Array.isArray(createdFixtures) && createdFixtures.length > 0;
      if (!fixtureLinked) {
        console.error(`[LEDPlaylistView] WARNING: Row ${record.id} created but Fixtures field is empty! Fixture key: ${fixtureKey}`);
        alert(`Content was added but the fixture link failed — the row may not appear in the correct round view.\n\nPlease check Airtable and manually set the Fixtures field to "${fixtureKey}" on the new row.`);
      }
      
      const created = [record];

      // Update local state with the created record
      const newSlots = created.map((r) => ({
        id: r.id,
        playlistId: pickerFixture.id,
        breakName: pickerQuarters.join(", "),
        position: 0,
        client: contentItem.client || CLUB_NAME,
        contentName: contentItem.name || r.fields?.["Name"] || "",
        contentId: contentItem.contentId || "",
        slotType: "club",
        category: pickerCategory,
        duration: contentItem.duration ? `${contentItem.duration}s` : "30s",
        status: r.fields?.["Content Status"] || "New Submission",
        rotations: pickerPlays,
        thumbnail: contentItem.thumbnail || "",
        destination: contentItem.destination || "",
        _fromClubLed: true,
      }));
      setSlots((prev) => [...prev, ...newSlots]);
      setPickerFixture(null);

      console.log(`[LEDPlaylistView] Created ${created.length} club LED rows in club table for fixture ${pickerFixture.id}`);
    } catch (err) {
      console.error("[LEDPlaylistView] Add error:", err);
      alert(`Failed to add content: ${err.message}`);
    } finally {
      setAdding(false);
    }
  }, [pickerFixture, adding, pickerPlays, pickerQuarters, pickerCategory]);

  // ─── Get slots for a fixture ───
  const getSlotsForFixture = (fixture) => {
    return slots.filter((s) => s.playlistId === fixture.id);
  };

  // ─── Filtered content for picker ───
  const filteredContent = pickerSearch
    ? content.filter((c) =>
        c.name.toLowerCase().includes(pickerSearch.toLowerCase()) ||
        c.contentId.toLowerCase().includes(pickerSearch.toLowerCase())
      )
    : content;

  // ─── Filtered fixtures ───
  const visibleFixtures = (!selectedRound || selectedRound === "ALL")
    ? fixtures
    : fixtures.filter((f) => f.round === selectedRound);

  // Play count for visible fixtures — must match what's physically on screen
  const visibleSlotCount = visibleFixtures.reduce((sum, f) => {
    const fixtureSlots = getSlotsForFixture(f);
    const grouped = {};
    fixtureSlots.forEach((s) => {
      const key = s.contentName || s.contentId || s.id;
      if (!grouped[key]) grouped[key] = { rotations: s.rotations || 1, status: s.status, contentName: s.contentName };
      // For multiple rows with the same name, keep the first row's rotations (that's what the dropdown shows)
    });
    // Filter out hidden example rows, sum rotations (same as section header logic)
    return sum + Object.values(grouped)
      .filter(g =>
        !(g.status || "").toLowerCase().includes("example") &&
        (g.contentName || "").toLowerCase() !== "example content"
      )
      .reduce((s, g) => s + (g.rotations || 1), 0);
  }, 0);

  // Push info text to header
  useEffect(() => {
    if (!onHeaderInfoChange) return;
    const info = visibleSlotCount > 0
      ? `${visibleSlotCount} club slot${visibleSlotCount !== 1 ? "s" : ""} across ${visibleFixtures.length} fixture${visibleFixtures.length !== 1 ? "s" : ""}`
      : `${visibleFixtures.length} fixture${visibleFixtures.length !== 1 ? "s" : ""} — no content allocated`;
    onHeaderInfoChange((prev) => ({ ...prev, infoText: info }));
  }, [visibleSlotCount, visibleFixtures.length, onHeaderInfoChange]);


  // ─── Inline edit: toggle a quarter on/off for a row ───

  // ─── Preview handler — parse grouped asset links and open modal ───
  const handlePreviewClick = useCallback((item) => {
    const asset = assetMap[item.contentId] || assetMap[item.contentName];
    if (!asset) return;
    
    const dest = (asset.destination || item.destination || "").toLowerCase();
    const raw = asset.groupedAssetLinks;
    if (!raw) return;
    
    // Parse grouped asset links (JSON string with level file URLs)
    const stripWraps = (s) => (s || "").replace(/^_+/, "").replace(/_+$/, "").trim();
    let ribbonItems = [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        ribbonItems = parsed
          .filter(item => item && (item.downloadUrl || item.url))
          .map(item => ({
            name: item.name || item.fileName || "",
            downloadUrl: stripWraps(item.downloadUrl) || stripWraps(item.url),
          }));
      }
    } catch {
      // Try markdown link format: [name](url)
      const mdRegex = /\[([^\]]*)\]\(([^)]+)\)/g;
      let match;
      while ((match = mdRegex.exec(raw)) !== null) {
        ribbonItems.push({ name: match[1], downloadUrl: stripWraps(match[2]) });
      }
    }
    
    if (ribbonItems.length === 0) return;
    
    const isTiled = dest.includes("tiled");
    const is360 = dest.includes("360");
    
    if (isTiled) {
      setActivePreview({ type: "tiled", asset: { name: item.contentName, destination: asset.destination }, ribbonItems });
    } else if (is360) {
      setActivePreview({ type: "360", asset: { name: item.contentName, destination: asset.destination }, ribbonItems });
    }
  }, [assetMap]);
  // eslint-disable-next-line no-unused-vars
  const handleToggleQuarter = useCallback(async (item, quarter) => {
    const currentBreaks = item.breakSummary ? item.breakSummary.split(",").map(q => q.trim()).filter(Boolean) : [];
    const newBreaks = currentBreaks.includes(quarter)
      ? currentBreaks.filter(q => q !== quarter)
      : [...currentBreaks, quarter].sort();
    
    // Optimistic update
    setSlots((prev) => prev.map((s) =>
      item.ids.includes(s.id) ? { ...s, breakName: newBreaks.join(", ") } : s
    ));
    
    // Write to Airtable
    for (const id of item.ids) {
      try {
        await updateLedRow(id, { breaks: newBreaks });
      } catch (err) {
        console.error(`[LEDPlaylistView] Failed to update quarters for ${id}:`, err);
      }
    }
  }, []);

  // ─── Inline edit: change rotation count for a row ───
  const handleChangeRotations = useCallback(async (item, newCount) => {
    // Auto-set quarters based on new rotation count
    let autoQuarters;
    if (newCount <= 1) autoQuarters = ["Q1"];
    else if (newCount === 2) autoQuarters = ["Q1", "Q2"];
    else if (newCount === 3) autoQuarters = ["Q1", "Q2", "Q3"];
    else autoQuarters = ["Q1", "Q2", "Q3", "Q4"];

    // Optimistic update
    setSlots((prev) => prev.map((s) =>
      item.ids.includes(s.id) ? { ...s, rotations: newCount, breakName: autoQuarters.join(", ") } : s
    ));
    
    // Write both rotations and quarters to Airtable
    for (const id of item.ids) {
      try {
        await updateLedRow(id, { rotations: newCount, breaks: autoQuarters });
      } catch (err) {
        console.error(`[LEDPlaylistView] Failed to update rotations for ${id}:`, err);
      }
    }
  }, []);

  // ─── Render a single content row ───
  const renderRow = (item) => {
    // Hide Example Content placeholder rows — they only exist to keep Airtable views active
    const isExample = (item.status || "").toLowerCase().includes("example") || 
                      (item.contentName || "").toLowerCase() === "example content";
    if (isExample) return null;

    const thumb = thumbMap[item.contentId] || thumbMap[item.contentName] || null;
    const isApproved = (item.status || "").toLowerCase() === "approved";
    const isRejected = (item.status || "").toLowerCase() === "rejected";
    const isPending = !isApproved && !isRejected;
    const rowBg = isApproved ? "rgba(34,197,94,0.06)" : isRejected ? "rgba(239,68,68,0.06)" : "transparent";
    const borderColor = isApproved ? "rgba(34,197,94,0.15)" : isRejected ? "rgba(239,68,68,0.15)" : THEME.lineLight;

    return (
      <div key={item.ids[0]} style={{ ...S.contentRow, background: rowBg, borderBottomColor: borderColor }}>
        <div style={{ ...S.rowThumb, cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); handlePreviewClick(item); }} title="Click to preview">
          {thumb ? <img src={thumb} alt="" style={S.thumbImg} /> : <div style={S.thumbPlaceholder}>📺</div>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: FONTS.base, fontWeight: FONTS.medium, color: THEME.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {item.contentName || item.client || "—"}
            </span>
            {isApproved && <span style={S.statusApproved}>✓ Approved</span>}
            {isRejected && <span style={S.statusRejected}>✗ Rejected</span>}
            {isPending && <span style={S.statusPending}>Pending QA</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
            <span style={{ fontSize: FONTS.xs, color: THEME.textMuted }}>{item.client || "Club"}</span>
            {(() => {
              const displayDur = item.duration || "30s";
              const label = /^\d+(\.\d+)?$/.test(displayDur) ? `${displayDur}s` : displayDur;
              return <span style={{ fontSize: FONTS.xs, color: THEME.textMuted }}>· {label}</span>;
            })()}
            {(() => {
              const cType = parseContentType(item.contentName);
              const cStyle = CONTENT_TYPE_STYLE[cType];
              if (!cStyle) return null;
              return (
                <span style={{ display: "inline-block", padding: "1px 7px", fontSize: 10, fontWeight: 700, borderRadius: 4, letterSpacing: "0.04em", color: cStyle.color, background: cStyle.bg, border: `1px solid ${cStyle.border}` }}>
                  {cStyle.label}
                </span>
              );
            })()}
            {(() => {
              // Use exactly the same logic as the picker modal so both
              // views always agree. parseSlotSource takes (name, destination)
              // and checks for "-commercial" — simple, authoritative.
              //
              // On reload from Airtable the slot's destination is empty
              // (the club LED Entitlements table doesn't persist it),
              // so we look up the asset's destination from the already-
              // loaded `content` array via contentId. content IS the
              // picker's source data — whatever badge the picker would
              // show, the playlist row will show. If content hasn't
              // loaded yet (first render race), we fall back to asset
              // map and then finally to name heuristics.
              let destForBadge = item.destination || "";
              if (!destForBadge && item.contentId) {
                const contentItem = content.find(c => c.contentId === item.contentId);
                if (contentItem) destForBadge = contentItem.destination || "";
              }
              if (!destForBadge) {
                const asset = assetMap[item.contentId] || assetMap[item.contentName] || null;
                destForBadge = asset?.destination || asset?.destinationText || "";
              }
              const src = parseSlotSource(item.contentName, destForBadge);
              const sStyle = SOURCE_BADGE[src];
              if (!sStyle) return null;
              return (
                <span style={{ display: "inline-block", padding: "1px 6px", fontSize: 10, fontWeight: 600, borderRadius: 4, color: sStyle.color, background: sStyle.bg, border: `1px solid ${sStyle.border}` }}>
                  {src}
                </span>
              );
            })()}
          </div>
        </div>
        <span style={{ width: 120, fontSize: FONTS.xs, color: THEME.textSecondary, fontFamily: "monospace" }}>
          {item.contentId}
        </span>
        {/* Quarter toggles hidden per commercial request */}
        <span style={{ width: 70, textAlign: "center", position: "relative" }}>
          <select
            value={item.rotations || item.count || 1}
            onChange={(e) => { e.stopPropagation(); handleChangeRotations(item, parseInt(e.target.value)); }}
            onClick={(e) => e.stopPropagation()}
            style={{
              appearance: "none", WebkitAppearance: "none",
              background: THEME.primaryLight, color: THEME.primary,
              border: `1px solid ${THEME.primary}33`, borderRadius: 12,
              padding: "3px 8px 3px 10px", fontSize: 12, fontWeight: 700,
              cursor: "pointer", textAlign: "center", width: 52,
            }}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>×{n}</option>
            ))}
          </select>
        </span>
        <div style={{ width: isLumen8Staff ? 240 : 36, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          {isLumen8Staff ? (
            <>
              {isPending && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); handleQAAction(item, "Approved"); }} disabled={qaProcessing === item.ids[0]} style={{ ...S.qaApproveBtn, opacity: qaProcessing === item.ids[0] ? 0.5 : 1 }}>✓ Approve</button>
                  <button onClick={(e) => { e.stopPropagation(); handleQAAction(item, "Rejected"); }} disabled={qaProcessing === item.ids[0]} style={{ ...S.qaRejectBtn, opacity: qaProcessing === item.ids[0] ? 0.5 : 1 }}>✗ Reject</button>
                </>
              )}
              {isApproved && (
                <button onClick={(e) => { e.stopPropagation(); handleQAAction(item, "Pending QA"); }} disabled={qaProcessing === item.ids[0]} style={{ ...S.qaUnlockBtn, opacity: qaProcessing === item.ids[0] ? 0.5 : 1 }}>🔓 Unlock</button>
              )}
              {isRejected && (
                <button onClick={(e) => { e.stopPropagation(); handleQAAction(item, "Approved"); }} disabled={qaProcessing === item.ids[0]} style={{ ...S.qaApproveBtn, opacity: qaProcessing === item.ids[0] ? 0.5 : 1 }}>↩ Re-approve</button>
              )}
              <button onClick={(e) => { e.stopPropagation(); handleDeleteSlots(item); }} style={S.deleteBtn} title="Delete">🗑️</button>
            </>
          ) : (
            isApproved ? <span style={{ fontSize: 14 }}>🔒</span> : (
              <button onClick={(e) => { e.stopPropagation(); handleDeleteSlots(item); }} style={S.deleteBtn} title="Remove">🗑️</button>
            )
          )}
        </div>
      </div>
    );
  };

  // ─── Render ───
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: THEME.textMuted, fontFamily: FONT }}>
        <div style={{ fontSize: FONTS.base }}>Loading LED Playlist…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
        <div style={{ color: THEME.textSecondary, marginBottom: 12 }}>{error}</div>
        <button onClick={fetchData} style={S.retryBtn}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ padding: "0 0 48px" }}>
      {visibleFixtures.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: THEME.textMuted }}>
          {fixtures.length === 0
            ? `No fixtures found for ${CLUB_NAME || "this club"}.`
            : `No fixtures for ${selectedRound}.`}
        </div>
      )}

      {/* Fixture list */}
      {visibleFixtures.map((fixture) => {
        const fixtureSlots = getSlotsForFixture(fixture);
        const vb = venueBadge(fixture.venue);
        const dateStr = formatDate(fixture.date);

        // Group slots by content name
        const grouped = {};
        fixtureSlots.forEach((s) => {
          const key = s.contentName || s.contentId || s.id;
          if (!grouped[key]) grouped[key] = { ...s, count: 0, ids: [], breaks: new Set() };
          // Club LED rows store play count in the Rotations field (1 row = N plays)
          // Commercial slots are 1 row per play (count by rows)
          grouped[key].count += s.rotations || 1;
          grouped[key].ids.push(s.id);
          if (s.breakName) {
            // breakName may be "Q1, Q2, Q3" — split into individual quarters
            s.breakName.split(",").map(q => q.trim()).filter(Boolean).forEach(q => grouped[key].breaks.add(q));
          }
          if (!grouped[key].status && s.status) grouped[key].status = s.status;
        });
        const groupedList = Object.values(grouped).map((item) => ({
          ...item,
          breakSummary: [...item.breaks].sort().join(", "),
        }));

        return (
          <div key={fixture.id} style={S.fixtureCard}>
            {/* Fixture header */}
            <div style={S.fixtureHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                <span style={S.roundBadge}>{fixture.round || "—"}</span>
                <span style={{ fontSize: FONTS.base, fontWeight: FONTS.semibold, color: THEME.textOnDark }}>
                  {fixture.homeTeam}
                </span>
                <span style={{ fontSize: FONTS.sm, color: "rgba(255,255,255,0.4)" }}>v</span>
                <span style={{ fontSize: FONTS.base, fontWeight: FONTS.semibold, color: THEME.textOnDark }}>
                  {fixture.awayTeam}
                </span>
                {dateStr && (
                  <span style={{ fontSize: FONTS.xs, color: "rgba(255,255,255,0.45)", marginLeft: 4 }}>
                    {dateStr}
                  </span>
                )}
                <span style={{
                  fontSize: FONTS.xs, fontWeight: FONTS.bold, padding: "2px 8px",
                  borderRadius: 4, background: vb.bg, color: vb.color,
                }}>
                  {vb.label}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button onClick={() => openPicker(fixture)} style={S.addBtn}>
                  + Add Content
                </button>
              </div>
            </div>

            {/* Allocation summary bar */}
            {fixtureSlots.length > 0 && (() => {
              // Filter out hidden example rows so tally matches what's physically on screen
              const isVisible = (item) =>
                !(item.status || "").toLowerCase().includes("example") &&
                (item.contentName || "").toLowerCase() !== "example content";
              const visibleItems = groupedList.filter(isVisible);
              // Sum exactly what the PLAYS column shows per row: the accumulated count from grouping
              const totalPlays = visibleItems.reduce((sum, item) => {
                const displayed = item.count || item.rotations || 1;
                return sum + displayed;
              }, 0);
              const mins = slotsToMinutes(totalPlays);
              const approvedCount = visibleItems.filter(item => (item.status || "").toLowerCase() === "approved").length;
              const pendingCount = visibleItems.filter(item => !item.status || item.status === "Pending QA" || item.status === "New Submission").length;
              const rejectedCount = visibleItems.filter(item => (item.status || "").toLowerCase() === "rejected").length;
              const breakSet = new Set(fixtureSlots.map(s => s.breakName).filter(Boolean));
              const breakCount = breakSet.size;

              return (
                <div style={{
                  display: "flex", alignItems: "center", gap: 16, padding: "10px 16px",
                  background: THEME.surface2, borderBottom: `1px solid ${THEME.lineLight}`,
                  flexWrap: "wrap",
                }}>
                  {/* Total slots + minutes */}
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ fontSize: FONTS.lg, fontWeight: FONTS.extrabold, color: THEME.primary }}>
                      {totalPlays}
                    </span>
                    <span style={{ fontSize: FONTS.sm, color: THEME.textSecondary }}>
                      play{totalPlays !== 1 ? "s" : ""}
                    </span>
                    <span style={{ fontSize: FONTS.xs, color: THEME.textMuted }}>
                      ({mins} min)
                    </span>
                  </div>

                  {/* Break distribution */}
                  <span style={{ fontSize: FONTS.xs, color: THEME.textMuted }}>
                    {breakCount} break{breakCount !== 1 ? "s" : ""}
                  </span>

                  <div style={{ flex: 1 }} />

                  {/* Status pills */}
                  {approvedCount > 0 && (
                    <span style={{
                      fontSize: FONTS.xs, fontWeight: FONTS.bold, color: "#16a34a",
                      background: "rgba(34,197,94,0.12)", padding: "3px 10px",
                      borderRadius: 12, whiteSpace: "nowrap",
                    }}>
                      ✓ {approvedCount} approved
                    </span>
                  )}
                  {pendingCount > 0 && (
                    <span style={{
                      fontSize: FONTS.xs, fontWeight: FONTS.bold, color: "#d97706",
                      background: "rgba(217,119,6,0.1)", padding: "3px 10px",
                      borderRadius: 12, whiteSpace: "nowrap",
                    }}>
                      ◷ {pendingCount} pending
                    </span>
                  )}
                  {rejectedCount > 0 && (
                    <span style={{
                      fontSize: FONTS.xs, fontWeight: FONTS.bold, color: "#dc2626",
                      background: "rgba(239,68,68,0.12)", padding: "3px 10px",
                      borderRadius: 12, whiteSpace: "nowrap",
                    }}>
                      ✗ {rejectedCount} rejected
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Content rows — split by category */}
            {groupedList.length === 0 ? (
              <div style={S.emptyRow}>
                <span style={{ color: THEME.textMuted, fontSize: FONTS.sm }}>
                  No content allocated — click "+ Add Content" to get started
                </span>
              </div>
            ) : (() => {
              const inGameItems = groupedList.filter(item => (item.category || "").includes("In-Game") || (item.category || "").includes("Commercial"));
              const bonusItems = groupedList.filter(item => (item.category || "").includes("Bonus"));
              // Items with no category default to In-Game
              const uncategorised = groupedList.filter(item => !inGameItems.includes(item) && !bonusItems.includes(item));
              const inGameAll = [...inGameItems, ...uncategorised];

              const renderSection = (title, items, color) => (
                <div key={title}>
                  <div style={{
                    padding: "8px 16px", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em",
                    textTransform: "uppercase", color, background: "rgba(0,0,0,0.03)",
                    borderBottom: `1px solid ${THEME.lineLight}`, borderTop: `1px solid ${THEME.lineLight}`,
                  }}>
                    {title} <span style={{ fontWeight: 400, opacity: 0.6 }}>({(() => {
                      const real = items.filter(i => !(i.status || "").toLowerCase().includes("example") && (i.contentName || "").toLowerCase() !== "example content");
                      return `${real.length} content, ${real.reduce((s, i) => s + (i.rotations || 1), 0)} plays`;
                    })()})</span>
                  </div>
                  {/* Table header */}
                  <div style={S.tableHeader}>
                    <span style={{ flex: 1, minWidth: 0 }}>CONTENT</span>
                    <span style={{ width: 120 }}>CONTENT ID</span>
                    {/* QUARTERS column hidden per commercial request */}
                    <span style={{ width: 70, textAlign: "center" }}>PLAYS</span>
                    <span style={{ width: isLumen8Staff ? 240 : 36, textAlign: "center", fontSize: 10, color: THEME.textMuted }}>
                      {isLumen8Staff ? "QA ACTIONS" : ""}
                    </span>
                  </div>
                  {items.map((item) => renderRow(item))}
                </div>
              );

              return (
                <div>
                  {inGameAll.length > 0 && renderSection("In-Game LED Commercial Entitlements", inGameAll, THEME.primary)}
                  {bonusItems.length > 0 && renderSection("Bonus Entitlements", bonusItems, "#f59e0b")}
                </div>
              );
            })()}
          </div>
        );
      })}
      {/* ─── Asset Picker Modal ─── */}
      {pickerFixture && (
        <div style={S.overlay} onClick={() => !adding && setPickerFixture(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div style={S.modalHeader}>
              <div>
                <div style={{ fontSize: FONTS.md, fontWeight: FONTS.bold, color: THEME.textOnDark }}>
                  Add LED Content
                </div>
                <div style={{ fontSize: FONTS.sm, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                  {pickerFixture.round} · {pickerFixture.homeTeam} v {pickerFixture.awayTeam}
                </div>
              </div>
              <button onClick={() => setPickerFixture(null)} style={S.closeBtn}>✕</button>
            </div>

            {/* Search + plays + quarters + category */}
            <div style={S.modalControls}>
              <input
                type="text"
                placeholder="Search content…"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                style={S.searchInput}
                autoFocus
              />
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: FONTS.sm, color: THEME.textSecondary }}>Plays:</span>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <button
                    key={n}
                    onClick={() => setPlaysWithQuarters(n)}
                    style={{
                      ...S.playsOption,
                      background: pickerPlays === n ? THEME.primary : THEME.surface2,
                      color: pickerPlays === n ? "#fff" : THEME.text,
                      fontWeight: pickerPlays === n ? 700 : 400,
                    }}
                  >
                    ×{n}
                  </button>
                ))}
              </div>
            </div>
            {/* Category selection (quarters hidden per commercial request) */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "8px 16px", borderBottom: `1px solid ${THEME.lineLight}`, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: FONTS.sm, color: THEME.textSecondary }}>Category:</span>
                {[
                  { key: "In-Game LED Commercial Entitlements", label: "In-Game" },
                  { key: "Bonus Entitlement", label: "Bonus" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setPickerCategory(key)}
                    style={{
                      padding: "3px 10px", borderRadius: 4, fontSize: 12, fontWeight: 600,
                      border: `1px solid ${pickerCategory === key ? THEME.primary : THEME.lineLight}`,
                      background: pickerCategory === key ? THEME.primaryLight : THEME.surface2,
                      color: pickerCategory === key ? THEME.primary : THEME.textMuted,
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content list */}
            <div style={S.modalList}>
              {contentLoading ? (
                <div style={{ padding: 32, textAlign: "center", color: THEME.textMuted }}>
                  Loading content…
                </div>
              ) : filteredContent.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: THEME.textMuted }}>
                  {pickerSearch ? "No matching content found" : "No LED content available"}
                </div>
              ) : (
                filteredContent.map((item) => (
                  <div
                    key={item.id}
                    style={S.pickerRow}
                    onClick={() => !adding && handleAddContent(item)}
                  >
                    <div style={S.pickerThumb}>
                      {item.thumbnail ? (
                        <img src={item.thumbnail} alt="" style={S.thumbImg} />
                      ) : (
                        <div style={S.thumbPlaceholder}>📺</div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: FONTS.base, fontWeight: FONTS.medium, color: THEME.text,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {item.name}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                        <span style={{ fontSize: FONTS.xs, color: THEME.textMuted }}>
                          {item.contentId}
                        </span>
                        {(() => {
                          const cType = parseContentType(item.name || item.destination);
                          const cStyle = CONTENT_TYPE_STYLE[cType];
                          if (!cStyle) return null;
                          return (
                            <span style={{
                              display: "inline-block", padding: "1px 7px", fontSize: 10, fontWeight: 700,
                              borderRadius: 4, letterSpacing: "0.04em",
                              color: cStyle.color, background: cStyle.bg, border: `1px solid ${cStyle.border}`,
                            }}>
                              {cStyle.label}
                            </span>
                          );
                        })()}
                        {(() => {
                          const src = parseSlotSource(item.name, item.destination);
                          const sStyle = SOURCE_BADGE[src];
                          if (!sStyle) return null;
                          return (
                            <span style={{
                              display: "inline-block", padding: "1px 6px", fontSize: 10, fontWeight: 600,
                              borderRadius: 4,
                              color: sStyle.color, background: sStyle.bg, border: `1px solid ${sStyle.border}`,
                            }}>
                              {src}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <span style={{
                      fontSize: FONTS.xs, fontWeight: FONTS.semibold, color: THEME.primary,
                      whiteSpace: "nowrap", padding: "4px 10px",
                      borderRadius: 6, background: THEME.primaryLight,
                    }}>
                      + Add ×{pickerPlays}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── LED Preview Modals ─── */}
      {activePreview?.type === "tiled" && (
        <RibbonPreviewModal
          asset={activePreview.asset}
          ribbonItems={activePreview.ribbonItems}
          onClose={() => setActivePreview(null)}
        />
      )}
      {activePreview?.type === "360" && (
        <ThreeSixtyPreviewModal
          asset={activePreview.asset}
          ribbonItems={activePreview.ribbonItems}
          onClose={() => setActivePreview(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const S = {
  fixtureCard: {
    margin: "12px 16px", borderRadius: THEME.radius,
    border: `1px solid ${THEME.line}`, background: THEME.surface,
    overflow: "hidden", boxShadow: THEME.shadow,
    fontFamily: FONT,
  },
  fixtureHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 16px", borderBottom: `1px solid ${THEME.line}`,
    background: THEME.dark, color: THEME.textOnDark,
  },
  roundBadge: {
    fontSize: FONTS.xs, fontWeight: FONTS.extrabold, color: THEME.primary,
    background: "rgba(47,214,181,0.15)", padding: "3px 10px",
    borderRadius: THEME.radiusSm, fontFamily: "monospace", letterSpacing: 0.5,
  },
  slotCount: {
    fontSize: FONTS.xs, fontWeight: FONTS.bold, color: THEME.primary,
    background: "rgba(47,214,181,0.12)", padding: "3px 10px",
    borderRadius: THEME.radiusFull,
  },
  addBtn: {
    fontSize: FONTS.sm, fontWeight: FONTS.semibold, color: "#111",
    background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
    border: "none",
    borderRadius: THEME.radiusSm, padding: "6px 16px", cursor: "pointer",
    transition: THEME.transitionFast, fontFamily: FONT,
    boxShadow: "0 1px 4px rgba(47,214,181,0.3)",
  },
  emptyRow: {
    padding: "20px 16px", textAlign: "center",
  },
  tableHeader: {
    display: "flex", alignItems: "center", padding: "6px 16px",
    fontSize: FONTS.xs, fontWeight: FONTS.bold, color: THEME.textMuted,
    letterSpacing: 0.8, borderBottom: `1px solid ${THEME.lineLight}`,
    background: THEME.tableHeader, fontFamily: FONT,
  },
  contentRow: {
    display: "flex", alignItems: "center", padding: "10px 16px",
    borderBottom: `1px solid ${THEME.lineLight}`, gap: 8,
    transition: THEME.transitionFast, fontFamily: FONT,
  },
  playsBadge: {
    fontSize: FONTS.xs, fontWeight: FONTS.bold, color: THEME.primary,
    background: THEME.primaryLight, padding: "2px 8px",
    borderRadius: 4, fontFamily: "monospace",
  },
  deleteBtn: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 14, padding: "2px 4px", opacity: 0.4,
    transition: THEME.transitionFast,
  },
  qaApproveBtn: {
    fontSize: 12, fontWeight: 700, color: "#fff", background: "#16a34a",
    border: "none", borderRadius: 6, padding: "5px 14px",
    cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif",
    boxShadow: "0 1px 4px rgba(22,163,74,0.3)", whiteSpace: "nowrap",
  },
  qaRejectBtn: {
    fontSize: 12, fontWeight: 700, color: "#fff", background: "#dc2626",
    border: "none", borderRadius: 6, padding: "5px 14px",
    cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif",
    boxShadow: "0 1px 4px rgba(220,38,38,0.3)", whiteSpace: "nowrap",
  },
  qaUnlockBtn: {
    fontSize: 12, fontWeight: 700, color: "#e2e8f0", background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, padding: "5px 14px",
    cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif", whiteSpace: "nowrap",
  },
  statusApproved: {
    fontSize: FONTS.xs, fontWeight: FONTS.bold, color: "#16a34a",
    background: "rgba(34,197,94,0.12)", padding: "2px 8px",
    borderRadius: 4, whiteSpace: "nowrap",
  },
  statusRejected: {
    fontSize: FONTS.xs, fontWeight: FONTS.bold, color: "#dc2626",
    background: "rgba(239,68,68,0.12)", padding: "2px 8px",
    borderRadius: 4, whiteSpace: "nowrap",
  },
  statusPending: {
    fontSize: FONTS.xs, fontWeight: FONTS.medium, color: "#d97706",
    background: "rgba(217,119,6,0.1)", padding: "2px 8px",
    borderRadius: 4, whiteSpace: "nowrap",
  },
  retryBtn: {
    fontSize: FONTS.base, fontWeight: FONTS.semibold, color: THEME.textOnPrimary,
    background: THEME.primary, border: "none",
    borderRadius: THEME.radiusSm, padding: "8px 24px", cursor: "pointer",
    fontFamily: FONT,
  },

  // Modal
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999,
  },
  modal: {
    width: 620, maxHeight: "85vh", background: THEME.surface,
    borderRadius: THEME.radiusLg, boxShadow: THEME.shadowXl, display: "flex",
    flexDirection: "column", overflow: "hidden", fontFamily: FONT,
  },
  modalHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 20px", borderBottom: `1px solid ${THEME.line}`,
    background: THEME.dark, color: THEME.textOnDark,
  },
  closeBtn: {
    background: "none", border: "none", fontSize: 18,
    color: "rgba(255,255,255,0.5)", cursor: "pointer", padding: "4px 8px",
  },
  modalControls: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 20px", gap: 12, borderBottom: `1px solid ${THEME.lineLight}`,
    background: THEME.tableHeader,
  },
  searchInput: {
    flex: 1, padding: "8px 12px", borderRadius: THEME.radiusSm,
    border: `1px solid ${THEME.line}`, fontSize: FONTS.base,
    outline: "none", background: THEME.surface,
    fontFamily: FONT,
  },
  playsOption: {
    width: 36, height: 30, borderRadius: THEME.radiusSm,
    border: "none", cursor: "pointer", fontSize: FONTS.sm,
    fontFamily: "monospace", transition: THEME.transitionFast,
  },
  modalList: {
    flex: 1, overflowY: "auto", maxHeight: "55vh",
    scrollbarWidth: "thin",
  },
  pickerRow: {
    display: "flex", alignItems: "center", padding: "10px 20px",
    borderBottom: `1px solid ${THEME.lineLight}`, cursor: "pointer",
    gap: 12, transition: THEME.transitionFast,
  },
  pickerThumb: {
    width: 48, height: 36, borderRadius: 4, overflow: "hidden",
    background: THEME.surface2, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  rowThumb: {
    width: 40, height: 30, borderRadius: 4, overflow: "hidden",
    background: THEME.surface2, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  thumbImg: {
    width: "100%", height: "100%", objectFit: "cover",
  },
  thumbPlaceholder: {
    fontSize: 16, opacity: 0.4,
  },
};
